import { createRouter, createWebHistory } from 'vue-router';
import { useAuth } from '../composables/useAuth';
import LoginView from '../views/LoginView.vue';
import BureauView from '../views/BureauView.vue';
import ViewerView from '../views/ViewerView.vue';
import ProductionView from '../views/ProductionView.vue';
import ExpeditionsView from '../views/ExpeditionsView.vue';
import InstallationsView from '../views/InstallationsView.vue';
import UsersView from '../views/UsersView.vue';
import UserDetailView from '../views/UserDetailView.vue';

function getHomeRouteName(role?: string): 'bureau' | 'usine' | 'logistique' | 'site-chantier' {
  switch (role) {
    case 'USINE':
    case 'FACTORY':
      return 'usine';
    case 'LOGISTIQUE':
      return 'logistique';
    case 'CHANTIER':
    case 'SUPERVISION':
    case 'SITE':
      return 'site-chantier';
    case 'ADMIN':
    case 'BUREAU':
    case 'OFFICE':
    default:
      return 'bureau';
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { requiresAuth: false }
    },
    {
      path: '/',
      redirect: '/bureau'
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      redirect: '/bureau',
      meta: { requiresAuth: true }
    },
    {
      path: '/bureau',
      name: 'bureau',
      component: BureauView,
      meta: { requiresAuth: true, allowedRoles: ['ADMIN', 'BUREAU', 'OFFICE'] }
    },
    {
      path: '/projets',
      name: 'projets',
      redirect: '/bureau',
      meta: { requiresAuth: true }
    },
    {
      path: '/viewer',
      name: 'viewer',
      component: ViewerView,
      meta: { requiresAuth: true }
    },
    {
      path: '/commandes',
      name: 'commandes',
      redirect: '/bureau',
      meta: { requiresAuth: true }
    },
    {
      path: '/usine',
      name: 'usine',
      component: ProductionView,
      meta: { requiresAuth: true, allowedRoles: ['ADMIN', 'USINE', 'FACTORY'] }
    },
    {
      path: '/production',
      name: 'production',
      redirect: '/usine',
      meta: { requiresAuth: true }
    },
    {
      path: '/materiaux',
      name: 'materiaux',
      redirect: '/usine',
      meta: { requiresAuth: true }
    },
    {
      path: '/logistique',
      name: 'logistique',
      component: ExpeditionsView,
      meta: { requiresAuth: true, allowedRoles: ['ADMIN', 'LOGISTIQUE'] }
    },
    {
      path: '/expeditions',
      name: 'expeditions',
      redirect: '/logistique',
      meta: { requiresAuth: true }
    },
    {
      path: '/site-chantier',
      name: 'site-chantier',
      component: InstallationsView,
      meta: { requiresAuth: true, allowedRoles: ['ADMIN', 'CHANTIER', 'SUPERVISION', 'SITE'] }
    },
    {
      path: '/receptions',
      name: 'receptions',
      redirect: '/site-chantier',
      meta: { requiresAuth: true }
    },
    {
      path: '/installations',
      name: 'installations',
      redirect: '/site-chantier',
      meta: { requiresAuth: true }
    },
    {
      path: '/metriques',
      name: 'metriques',
      redirect: '/bureau',
      meta: { requiresAuth: true }
    },
    {
      path: '/users',
      name: 'users',
      component: UsersView,
      meta: { requiresAuth: true, requiresRole: 'ADMIN' }
    },
    {
      path: '/users/:id',
      name: 'user-detail',
      component: UserDetailView,
      meta: { requiresAuth: true, requiresRole: 'ADMIN' }
    },
    {
      path: '/projets/:id',
      name: 'projet-detail',
      component: () => import('../views/ProjetDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/commandes/:id',
      name: 'commande-detail',
      component: () => import('../views/CommandeDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/expeditions/:id',
      name: 'expedition-detail',
      component: () => import('../views/ExpeditionDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/receptions/:id',
      name: 'reception-detail',
      component: () => import('../views/ReceptionDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/production/:id',
      name: 'production-detail',
      component: () => import('../views/ProductionDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ]
});

// Garde de navigation pour vérifier l'authentification
router.beforeEach((to, from, next) => {
  const { isAuthenticated, user } = useAuth();
  const role = user.value?.role;
  
  if (to.meta.requiresAuth && !isAuthenticated.value) {
    next({ name: 'login' });
  } else if (to.name === 'login' && isAuthenticated.value) {
    next({ name: getHomeRouteName(role) });
  } else if (to.meta.requiresRole && role !== to.meta.requiresRole) {
    next({ name: getHomeRouteName(role) });
  } else if (Array.isArray(to.meta.allowedRoles) && role && !to.meta.allowedRoles.includes(role)) {
    next({ name: getHomeRouteName(role) });
  } else {
    next();
  }
});

export default router;
